import * as React from "react";
import { IBasePickerSuggestionsProps, Label, IPersonaProps, NormalPeoplePicker, PeoplePickerItemSuggestion, IPeoplePickerItemSuggestionStyles } from "@fluentui/react";
import { getSharePointSiteUsersById, searchSharePointSiteUsers, searchSharePointGroup } from "../../services/SharePointRestServices";
import { IPeoplePickerProps } from "../../interfaces/props/IPeoplePickerProps";
import { SharePointMetadataBooleans } from "../../config/sharepointConstants";
import { validateEmail } from "../helpers/validateEmail";
import { IPerson } from "../../interfaces/IPersonResult";
import { searchPeople } from "../../services/GraphService";
import { ISharePointSiteUser, ISiteUser } from "../../interfaces/ISiteUser";

const suggestionProps: IBasePickerSuggestionsProps = {
  suggestionsHeaderText: "Suggested People",
  mostRecentlyUsedHeaderText: "Suggested Contacts",
  noResultsFoundText: "No results found",
  loadingText: "Loading",
  showRemoveButtons: true,
  suggestionsAvailableAlertText: "People Picker Suggestions available",
  suggestionsContainerAriaLabel: "Suggested contacts"
};

const suggestionStyles: Partial<IPeoplePickerItemSuggestionStyles> = {
  personaWrapper: {
    width: "auto"
  }
}

export const PeoplePicker: React.FunctionComponent<IPeoplePickerProps> = ({
  webUrl,
  displayName,
  isRequired,
  error,
  itemLimit,
  updateInputValidationStatus,
  inputValidator,
  inputValue
}) => {
  const [selectedPeople, setSelectedPeople] = React.useState<IPersonaProps[] | undefined>();

  const pickerRef = React.useRef<any>(null);

  React.useEffect(() => {
    if (selectedPeople === undefined && inputValue?.value) {
      (async () => {
        let initialValues: number[];
        if (Array.isArray(inputValue?.value)) {
          initialValues = inputValue.value.map(x => x);
        }
        else if (inputValue?.value && parseInt(inputValue.value)) {
          initialValues = [inputValue.value];
        }

        if (Array.isArray(initialValues) && initialValues.length > 0) {
          const initialUsers: ISiteUser[] = await getSharePointSiteUsersById(webUrl, initialValues);
          setSelectedPeople(mapSiteUsers(initialUsers));
        }
        else {
          setSelectedPeople([]);
        }
      })();
    }
  }, [webUrl, inputValue?.value]);

  const onFilterChanged = async (
    filterText: string,
    currentPersonas: IPersonaProps[]
  ): Promise<IPersonaProps[]> => {
    if (filterText) {
      try {
        let filteredPersonas: IPersonaProps[];

        const groupToSearch: string = inputValue?.xml?.UserSelectionScope;
        const allowGroups: boolean = inputValue?.xml?.UserSelectionMode !== "0" && inputValue?.xml?.UserSelectionMode !== "PeopleOnly";

        if (!groupToSearch || groupToSearch == "0") {
          let people: IPerson[]
          let siteUsers: ISiteUser[];
          let siteGroupUsers: ISiteUser[];

          [people, siteUsers, siteGroupUsers] = await Promise.all([
            searchPeople(filterText, allowGroups),
            searchSharePointSiteUsers(webUrl, filterText, allowGroups),
            searchSharePointGroup(webUrl, "0", allowGroups, filterText)
          ]);

          filteredPersonas = mapSiteUsers(siteUsers.concat(siteGroupUsers ?? []));

          if (siteUsers?.length > 0 && siteGroupUsers?.length > 0) {
            const uniqueEntries = new Set();
            filteredPersonas = filteredPersonas.filter(obj => {
              if (uniqueEntries.has(obj.secondaryText ?? obj.text)) {
                return false;
              } else {
                uniqueEntries.add(obj.secondaryText ?? obj.text);
                return true;
              }
            });
          }

          const suggestedPeople: IPersonaProps[] = mapPeople(people);

          filteredPersonas.push(...removeDuplicates(suggestedPeople, filteredPersonas));
        }
        else {
          const siteGroupUsers: ISiteUser[] = await searchSharePointGroup(webUrl, groupToSearch, allowGroups, filterText);
          filteredPersonas = mapSiteUsers(siteGroupUsers);
        }

        return sortResults(filterText, removeDuplicates(filteredPersonas, currentPersonas));
      } catch (error) {
        console.error(error);
      }
    }
    return [];
  };

  const handleOnChange = (items?: IPersonaProps[]) => {
    setSelectedPeople(items ?? []);
    const persons = (items as any[]).map<ISharePointSiteUser>(item => ({ id: item.userId ?? 0, email: item.secondaryText ?? item.logonName }));
    updateInputValidationStatus(inputValidator.validate(persons));
  };

  const onRenderSuggestionsItem = React.useCallback((personaProps, suggestionsProps) => (
    <PeoplePickerItemSuggestion
      personaProps={personaProps}
      suggestionsProps={suggestionsProps}
      styles={suggestionStyles} />
  ), []);

  const onBlur = React.useCallback((ev: React.FocusEvent<HTMLInputElement>) => {
    try {
      pickerRef.current?.input.current._updateValue("");
    } catch (error) { }
  }, []);

  return (
    <div>
      {displayName && <Label required={isRequired()}>{displayName}</Label>}
      <NormalPeoplePicker
        componentRef={pickerRef}
        disabled={inputValue?.xml?.ReadOnly === SharePointMetadataBooleans.true}
        onRenderSuggestionsItem={onRenderSuggestionsItem}
        onResolveSuggestions={onFilterChanged}
        getTextFromItem={getTextFromItem}
        pickerSuggestionsProps={suggestionProps}
        className={"ms-PeoplePicker"}
        key={"preselected"}
        itemLimit={itemLimit}
        removeButtonAriaLabel={"Remove"}
        selectedItems={selectedPeople ?? []}
        onChange={handleOnChange}
        onBlur={onBlur}
        resolveDelay={300}
      />
    </div>
  );
};

const mapSiteUsers = (siteUsers: ISiteUser[]): IPersonaProps[] => {
  return siteUsers
    .map(item => {
      let secondaryText: string;
      if (validateEmail(item.EMail)) {
        secondaryText = item.EMail;
      }
      else if (validateEmail(item.UserName)) {
        secondaryText = item.UserName;
      }

      return item.Id || secondaryText
        ? { userId: item.Id, text: item.Title, secondaryText, logonName: item.LogonName }
        : null;
    })
    .filter(item => item);
};

const mapPeople = (people: IPerson[]): IPersonaProps[] => {
  return people.map(person => {
    let secondaryText: string;
    if (validateEmail(person.userPrincipalName)) {
      secondaryText = person.userPrincipalName;
    }
    else if (person.scoredEmailAddresses?.length > 0 && validateEmail(person.scoredEmailAddresses[0]?.address)) {
      secondaryText = person.scoredEmailAddresses[0].address;
    }

    return secondaryText
      ? { text: person.displayName, secondaryText }
      : null;
  })
    .filter(person => person);
};

const sortResults = (input: string, data: IPersonaProps[]): IPersonaProps[] => {
  try {
    let first: IPersonaProps[] = [];
    let others: IPersonaProps[] = [];
    for (let i = 0; i < data.length; i++) {
      if (data[i].text.indexOf(input) == 0 || data[i].secondaryText?.indexOf(input) == 0) {
        first.push(data[i]);
      } else {
        others.push(data[i]);
      }
    }
    first.sort((a, b) => a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1);
    others.sort((a, b) => a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1);
    return (first.concat(others));
  } catch (error) {
    console.warn(error);
  }
  return data;
}

function removeDuplicates(personas: IPersonaProps[], possibleDupes: IPersonaProps[]) {
  return personas.filter(persona => !listContainsPersona(persona, possibleDupes));
}

function listContainsPersona(persona: IPersonaProps, personas: IPersonaProps[]) {
  if (!personas || !personas.length || personas.length === 0) {
    return false;
  }
  return personas.filter(
    item => item.secondaryText
      ? item.secondaryText.toLowerCase() === persona.secondaryText?.toLowerCase()
      : item.text.toLowerCase() === persona.text.toLowerCase()
  ).length > 0;
}

function getTextFromItem(persona: IPersonaProps): string {
  return persona.text as string;
}

export default PeoplePicker;
